import { useEffect, useRef, useState } from "react";
import { App as CapApp } from "@capacitor/app";
import { events as eventsApi, type Conversation } from "../api";
import { netInfo } from "../cockpit-ui";
import { loadPrefs } from "../prefs";
import { notify } from "../notify";

/** Dernier événement temps réel reçu (conversation visée + compteur) → signale au fil ouvert de recharger. */
export type LiveSignal = { conv: string; n: number };

export type RealtimeInputs = {
  /** Liste fraîche des conversations (résolution de la conv visée par une notification). */
  conversations: Conversation[];
  /** Conversation actuellement ouverte (gating des notifications). */
  selectedId: string | null;
  /** Recharge la liste des conversations. */
  loadConversations: () => void;
  /** Ouvre une conversation (clic sur une notification). */
  openConversation: (c: Conversation) => void;
  /** Marque une conversation lue (badge local + reçu de lecture plateforme). */
  markConvRead: (id: string) => void;
  /** Applique un changement de read-state poussé par le serveur. */
  onReadChanged: (p: {
    accountId?: string;
    conversationRemoteId: string;
    unreadCount: number;
    isUnread: boolean;
  }) => void;
};

export type RealtimeState = {
  live: LiveSignal;
  /** remoteId de conversation → « écrit… » en cours (expire seul après 6 s). */
  typing: Record<string, boolean>;
  /** remoteId d'utilisateur → en ligne. */
  presence: Record<string, boolean>;
};

// Temps réel (#21/#22) : un message rafraîchit la liste et, si c'est la
// conversation ouverte, recharge son fil (signal `live`). EventSource se
// reconnecte seul ; le polling de la liste reste un repli.
//
// L'abonnement SSE est monté UNE SEULE fois (deps []) : toutes les entrées
// (liste fraîche, sélection, callbacks) sont lues à travers une ref mise à
// jour à chaque rendu — se réabonner au flux à chaque changement d'état
// serait une régression (perte d'événements, reconnexions en boucle).
export function useRealtime(inputs: RealtimeInputs): RealtimeState {
  const [live, setLive] = useState<LiveSignal>({ conv: "", n: 0 });
  const [typing, setTyping] = useState<Record<string, boolean>>({});
  const [presence, setPresence] = useState<Record<string, boolean>>({});

  const inputsRef = useRef(inputs);
  useEffect(() => {
    inputsRef.current = inputs;
  });
  // Expiration des « écrit… » : un timer par conversation (remplace un polling).
  const typingTimers = useRef<Record<string, number>>({});

  useEffect(() => {
    let es: EventSource | null = null;
    const timers = typingTimers.current;

    const attach = (s: EventSource) => {
      s.addEventListener(eventsApi.MESSAGE_RECEIVED, (e: MessageEvent) => {
        const { loadConversations, openConversation, conversations, selectedId, markConvRead } =
          inputsRef.current;
        loadConversations();
        try {
          const p = JSON.parse(e.data) as {
            conversationId?: string;
            isOutgoing?: boolean;
            network?: string;
          };
          if (p.conversationId) setLive({ conv: p.conversationId, n: Date.now() });
          // Message ENTRANT dans la conversation OUVERTE et VISIBLE : on est en train
          // de la lire → marque lu aussitôt (vide le badge ET envoie le reçu à la
          // plateforme via markRead) ; sinon l'app afficherait un non-lu et la
          // plateforme nous croirait non-lus (#2). loadConversations a re-bumpé le
          // badge juste au-dessus : markConvRead le re-vide (optimiste + serveur).
          const inboundOpenVisible =
            !!p.conversationId &&
            p.isOutgoing === false &&
            p.conversationId === selectedId &&
            !document.hidden;
          if (inboundOpenVisible) markConvRead(p.conversationId!);
          // Notifie sur message ENTRANT quand l'onglet n'est pas au premier plan,
          // sauf si c'est la conversation déjà ouverte et visible.
          if (
            p.conversationId &&
            p.isOutgoing === false &&
            loadPrefs().notifications !== false &&
            (document.hidden || selectedId !== p.conversationId)
          ) {
            const conv = conversations.find((c) => c.id === p.conversationId);
            const label = netInfo(p.network ?? "").label;
            notify(conv?.title || label, `Nouveau message · ${label}`, () => {
              if (conv) openConversation(conv);
            });
          }
        } catch {
          /* payload non-JSON — ignore */
        }
      });
      s.addEventListener(eventsApi.CONNECTION_STATUS, () => inputsRef.current.loadConversations());
      s.addEventListener(eventsApi.TYPING, (e: MessageEvent) => {
        try {
          const p = JSON.parse(e.data) as { conversationRemoteId?: string; typing?: boolean };
          const id = p.conversationRemoteId;
          if (!id) return;
          if (timers[id]) clearTimeout(timers[id]);
          if (p.typing) {
            setTyping((t) => ({ ...t, [id]: true }));
            timers[id] = window.setTimeout(() => {
              setTyping((t) => (t[id] ? { ...t, [id]: false } : t));
            }, 6000);
          } else {
            setTyping((t) => (t[id] ? { ...t, [id]: false } : t));
          }
        } catch {
          /* ignore */
        }
      });
      s.addEventListener(eventsApi.PRESENCE, (e: MessageEvent) => {
        try {
          const p = JSON.parse(e.data) as { userRemoteId?: string; online?: boolean };
          if (p.userRemoteId) setPresence((pr) => ({ ...pr, [p.userRemoteId!]: !!p.online }));
        } catch {
          /* ignore */
        }
      });
      // Read-state en direct (lu sur le téléphone OU sur une autre session AOS) :
      // met à jour le badge non-lu de la conversation correspondante (match account+remoteId).
      s.addEventListener(eventsApi.READ_CHANGED, (e: MessageEvent) => {
        try {
          const p = JSON.parse(e.data) as {
            accountId?: string;
            conversationRemoteId?: string;
            unreadCount?: number;
            isUnread?: boolean;
          };
          if (!p.conversationRemoteId) return;
          inputsRef.current.onReadChanged({
            accountId: p.accountId,
            conversationRemoteId: p.conversationRemoteId,
            unreadCount: p.unreadCount ?? 0,
            isUnread: !!p.isUnread,
          });
        } catch {
          /* ignore */
        }
      });
    };

    const connect = () => {
      try {
        es = eventsApi.open();
      } catch {
        es = null; // EventSource indisponible → on reste sur le polling
        return;
      }
      attach(es);
    };
    connect();

    // Retour d'avant-plan (onglet revisible / app native réactivée) : iOS
    // suspend la WKWebView en arrière-plan et le flux SSE meurt souvent sans
    // événement d'erreur. On ré-établit l'EventSource s'il est fermé, puis on
    // resynchronise ce qui a pu être manqué : liste des conversations
    // (compteurs non-lus) + fil ouvert (signal `live`).
    const resync = () => {
      if (!es || es.readyState === EventSource.CLOSED) {
        es?.close();
        connect();
      }
      inputsRef.current.loadConversations();
      const sel = inputsRef.current.selectedId;
      if (sel) setLive({ conv: sel, n: Date.now() });
    };
    const onVisibility = () => {
      if (!document.hidden) resync();
    };
    document.addEventListener("visibilitychange", onVisibility);
    // Natif : appStateChange couvre les cas où visibilitychange ne tire pas
    // (suspension WKWebView). Sur le web le plugin émet aussi via visibility,
    // resync() est idempotent donc un double appel est sans effet de bord.
    const sub = CapApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) resync();
    });

    return () => {
      es?.close();
      document.removeEventListener("visibilitychange", onVisibility);
      void sub
        .then((h) => h.remove())
        .catch(() => {
          /* déjà détaché */
        });
      for (const k in timers) clearTimeout(timers[k]);
    };
  }, []);

  return { live, typing, presence };
}
